import { allTimezones, useTimezoneSelect } from 'react-timezone-select';
import Switch from "react-switch";
import { useEffect, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import RangeSlider from '../primitives/RangeSlider';
import { useDatabase } from '../../contexts/DatabaseContext';
import { Button } from '../primitives';
import { useTranslation } from '../../hooks/useTranslation';
import { LIMITS } from '../../Limits';

export default function BaselineSettings({ days, workspaceId, workspaceType, config, setConfig, baseSettings, setBaseSettings, hasChangesBaseline, requestedVisualTrigger }) {

    const [loading, setLoading] = useState(false);
    const [errors, setErrors] = useState({ chatTitle: null, newMessageTitle: null, welcomeMsg: null });
    const [buttonText, setButtonText] = useState('Update Settings');
    const { updateWorkspaceConfigDoc, createBaselineActionEvent } = useDatabase();
    const t = useTranslation();

    const BusinessHoursOptions = ["Always Active", "Custom Hours", "Custom Week"];
    const { mobile } = useOutletContext();

    const handleSubmit = async () => {
        try {
            setLoading(true);
            const days_open = days.map(day => baseSettings.businessHours[day].isOpen);
            createBaselineActionEvent(
                workspaceType,
                workspaceId,
                config.chatTitle !== baseSettings.chatTitle,
                config.newMessageTitle !== baseSettings.newMessageTitle,
                config.welcomeMsg !== baseSettings.welcomeMsg,
                baseSettings.contactSetting,
                baseSettings.businessHourSetting,
                baseSettings.showOutsideHours,
                config.offlineWelcomeMsg !== baseSettings.offlineWelcomeMsg,
                days_open,
            );
            await updateWorkspaceConfigDoc(workspaceId, { ...baseSettings });
            setConfig(prev => ({ ...prev, ...baseSettings }));
        } catch (error) {
            window.dispatchEvent(new CustomEvent('masterError', {
                detail: {
                    message: "An error occurred while updating the workspace config.",
                    action: "baseline",
                }
            }));
        } finally {
            setLoading(false);
            setButtonText('Published!');
            setTimeout(() => {
                setButtonText('Update Settings');
            }, 2000);
        }
    };

    function validateTextInput(field, value) {
        if (field === 'chatTitle') {
            return value.length <= LIMITS.chatTitle && value.length > 0;
        } else if (field === 'newMessageTitle') {
            return value.length <= LIMITS.newMessageTitle;
        } else if (field === 'welcomeMsg') {
            return value.length <= LIMITS.welcomeMsg && value.length > 0;
        } else if (field === 'offlineWelcomeMsg') {
            return value.length <= LIMITS.offlineWelcomeMsg && value.length > 0;
        }
        return true;
    }

    function handleTextInputChange(field, value) {
        if (field === 'newMessageTitle') {
            requestedVisualTrigger.current = 'popup';
        } else {
            requestedVisualTrigger.current = 'chat';
        }
        setBaseSettings((prev) => ({ ...prev, [field]: value }));
        if (validateTextInput(field, value)) {
            setErrors(prev => ({ ...prev, [field]: null }));
        } else {
            setErrors(prev => ({ ...prev, [field]: t(`${LIMITS[`${field}Error`]}`) }));
        }
    }

    function hasErrors() {
        return Object.values(errors).some(error => error !== null);
    }

    return (
        <div className="settings-container">
            <div className="settings-grid-container">
                <div className="input-settings-group">
                    <div className="input-settings-group-inner">
                        <label htmlFor="chatTitle" className="settings-input-label">Chat Title:</label>
                        <p className="settings-input-description">This is the title of the chat that will be displayed to the user.</p>
                        <input 
                            type="text" 
                            className={`settings-input ${errors?.chatTitle ? 'error' : ''}`} 
                            id="chatTitle" 
                            name="chatTitle" 
                            value={baseSettings?.chatTitle}
                            placeholder="Enter your title"
                            onChange={(e) => handleTextInputChange('chatTitle', e.target.value)}
                        />
                        {errors?.chatTitle && <p className="settings-input-error">{errors?.chatTitle}</p>}
                    </div>
                </div>

                <div className="input-settings-group">
                    <div className="input-settings-group-inner">
                        <label htmlFor="newMessageTitle" className="settings-input-label">New Message Popup Title:</label>
                        <p className="settings-input-description">This is the title of the new message popup that will be displayed to the user.</p>
                        <input
                            type="text"
                            className={`settings-input ${errors?.newMessageTitle ? 'error' : ''}`}
                            id="newMessageTitle"
                            name="newMessageTitle"
                            value={baseSettings?.newMessageTitle}
                            placeholder="Enter your new message title"
                            onChange={(e) => handleTextInputChange('newMessageTitle', e.target.value)}
                        />
                        {errors?.newMessageTitle && <p className="settings-input-error">{errors?.newMessageTitle}</p>}
                    </div>
                </div>

                <div className="input-settings-group">
                    <div className="input-settings-group-inner">
                        <label htmlFor="welcomeMsg" className="settings-input-label">Welcome Message:</label>
                        <p className="settings-input-description">This is the welcome message that will display with a newly opened chat.</p>
                        <input 
                            type="text" 
                            className={`settings-input ${errors?.welcomeMsg ? 'error' : ''}`} 
                            id="welcomeMsg" 
                            name="welcomeMsg" 
                            value={baseSettings?.welcomeMsg}
                            placeholder="Enter your welcome message"
                            onChange={(e) => handleTextInputChange('welcomeMsg', e.target.value)}
                        />
                        {errors?.welcomeMsg && <p className="settings-input-error">{errors?.welcomeMsg}</p>}
                    </div>
                </div>

                <div className="input-settings-group">
                    <div className="input-settings-group-inner">
                        <label className="settings-input-label">Contact Settings:</label>
                        <p className="settings-input-description">This is the setting for the contact that will be displayed to the user.</p>
                        <div className="settings-check">
                            <Switch 
                                onColor="#27ca52"
                                className="settings-check-input" 
                                checked={baseSettings?.contactSetting}
                                onChange={() => setBaseSettings(prev => ({ ...prev, contactSetting: !prev.contactSetting }))}
                            />
                            <label className="settings-check-label">Request Contact</label>
                        </div>
                    </div>
                </div>

                <div className={`input-settings-group ${!mobile ? 'grid-col-span-2' : ''}`}>
                    <div className="input-settings-group-inner">
                        <label className="settings-input-label">Hours of Activity</label>
                        <div className={mobile ? "input-settings-group-inner" : "input-settings-group-inner-row space-between"}>
                            {BusinessHoursOptions.map((setting, index) => (
                                <div key={setting} className="settings-hours-item">
                                    <input 
                                        className="settings-hours-input" 
                                        type="radio" 
                                        name="businessHourSettings"
                                        id={`businessHourSettings${setting}`} 
                                        value={index + 1}
                                        checked={baseSettings?.businessHourSetting === index + 1}
                                        onChange={() => setBaseSettings(prev => ({ ...prev, businessHourSetting: index + 1 }))}
                                    />
                                    <label className="settings-hours-label">{setting}</label>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>

            {baseSettings?.businessHourSetting > 1 && (
                <div className="input-settings-group">
                    <div className="input-settings-group-inner">
                        <label htmlFor="timezoneSelect" className="settings-input-label">Time Zone:</label>
                        <TimezoneSelect
                            className="settings-select"
                            value={baseSettings?.timezone}
                            onChange={(timezone) => setBaseSettings(prev => ({ ...prev, timezone }))}
                        />
                    </div> 
                </div>
            )}
    
            {baseSettings?.businessHourSetting > 1 && (
                <div className="settings-grid-container" style={{ marginTop: '10px' }}>
                    <div className="input-settings-group">
                        <div className="input-settings-group-inner">
                            <label className="settings-input-label">Show Chat Outside Business Hours:</label>
                            <p className="settings-input-description">When enabled, the chat will be visible outside business hours with a custom offline message.</p>
                            <div className="settings-check">
                                <Switch 
                                    onColor="#27ca52"
                                    className="settings-check-input" 
                                    checked={baseSettings?.showOutsideHours}
                                    onChange={() => setBaseSettings(prev => ({ ...prev, showOutsideHours: !prev.showOutsideHours }))}
                                />
                                <label className="settings-check-label">Show Outside Hours</label>
                            </div>
                        </div>
                    </div>

                    <div className={`input-settings-group${!baseSettings.showOutsideHours ? ' disabled' : ''}`} style={{ marginBottom: '20px' }}>
                        <div className="input-settings-group-inner">
                            <label htmlFor="offlineWelcomeMsg" className="settings-input-label">Offline Welcome Message:</label>
                            <p className="settings-input-description">This message will be displayed when the chat is accessed outside business hours.</p>
                            <input 
                                type="text" 
                                className={`settings-input ${errors?.offlineWelcomeMsg ? 'error' : ''}`} 
                                id="offlineWelcomeMsg" 
                                name="offlineWelcomeMsg" 
                                value={baseSettings?.offlineWelcomeMsg}
                                placeholder="Enter your offline welcome message"
                                onChange={(e) => handleTextInputChange('offlineWelcomeMsg', e.target.value)}
                            />
                            {errors?.offlineWelcomeMsg && <p className="settings-input-error">{errors?.offlineWelcomeMsg}</p>}
                        </div>
                    </div>
                </div>
            )}

            {baseSettings?.businessHourSetting === 2 && (
                <div className="settings-hours-container">
                    <SingleTimeSelector setBaseSettings={setBaseSettings} open={baseSettings.businessHours.open} close={baseSettings.businessHours.close} />
                </div>
            )}

            {baseSettings?.businessHourSetting === 3 && days.map((day) => (
                <TimeSelector key={day} day={day} isOpen={baseSettings.businessHours[day].isOpen} setBaseSettings={setBaseSettings} open={baseSettings.businessHours[day].open} close={baseSettings.businessHours[day].close}/>
            ))}
            
            <Button className="submit settings" 
                onClick={handleSubmit} 
                loading={loading}
                loadingFinish={buttonText === 'Published!'}
                disabled={!hasChangesBaseline || hasErrors()}
            >
                {buttonText}
            </Button>
        </div>
    );
}

function TimezoneSelect({ value, onChange, ...rest }) {
    const { options, parseTimezone } = useTimezoneSelect({ labelStyle: 'original', timezones: allTimezones, displayValue: 'UTC' });
    
    useEffect(() => {
        if (typeof value === 'string') {
            onChange(parseTimezone(value));
        }
    }, [value]);

    return (
        <select
            value={typeof value === 'object' ? value?.value : value}
            onChange={(e) => onChange(parseTimezone(e.target.value))}
            {...rest}
        >
            {options.map((option) => (
                <option key={option.value} value={option.value}>
                    {option.label}
                </option>
            ))}
        </select>
    );
}

function TimeSelector({ day, isOpen, setBaseSettings, open, close }) {
    
    const handleChange = (newRange) => {
        const [start, end] = newRange;
        
        // Prevent sliders from crossing each other
        if (start >= end) {
            return;
        }
        
        setBaseSettings(prev => ({ ...prev, businessHours: { ...prev.businessHours, [day]: { isOpen, open: start, close: end } } }));
    };

    const formatTime = (time) => {
        const hours = Math.floor(time);
        const minutes = Math.round((time % 1) * 60);
        const period = hours >= 12 && hours < 24 ? 'PM' : 'AM';
        const displayHours = hours === 0 ? 12 : hours > 12 ? hours - 12 : hours;
        return `${displayHours}:${minutes.toString().padStart(2, '0')} ${period}`;
    };

    return (
        <div className="time-selector-container">
            <Switch
                onColor="#27ca52"
                checked={isOpen}
                onChange={() => setBaseSettings(prev => ({ ...prev, businessHours: { ...prev.businessHours, [day]: { isOpen: !isOpen, open: open, close: close } } }))}
            />
            <label className="settings-check-label">{day}</label>
            <div className="single-time-selector">
                <RangeSlider
                    min={0}
                    max={24}
                    step={0.25}
                    defaultLeftValue={9}
                    defaultRightValue={17}
                    onChange={({ left, right }) => { handleChange([left, right]) }}
                    />
                <div className="time-range-display">
                    <span className="time-range-display-item">{formatTime(open)}</span>
                    <span className="time-range-display-item">{formatTime(close)}</span>
                </div>
            </div>
        </div>
    );
}

function SingleTimeSelector({ setBaseSettings, open, close }) {

    const handleChange = (newRange) => {
        const [start, end] = newRange;
        
        // Prevent sliders from crossing each other
        if (start >= end) {
            return;
        }
        
        setBaseSettings(prev => ({ ...prev, businessHours: { ...prev.businessHours, open: start, close: end } }));
    };

    const formatTime = (time) => {
        const hours = Math.floor(time);
        const minutes = Math.round((time % 1) * 60);
        const period = hours >= 12 && hours < 24 ? 'PM' : 'AM';
        const displayHours = hours === 0 ? 12 : hours > 12 ? hours - 12 : hours;
        return `${displayHours}:${minutes.toString().padStart(2, '0')} ${period}`;
    };

    return (
        <div className="single-time-selector">
            <RangeSlider
                    min={0}
                    max={24}
                    step={0.25}
                    defaultLeftValue={9}
                    defaultRightValue={17}
                    onChange={({ left, right }) => { handleChange([left, right]) }}
                    />
            <div className="time-range-display">
                <span className="time-range-display-item">{formatTime(open)}</span>
                <span className="time-range-display-item">{formatTime(close)}</span>
            </div>
        </div>
    );
}
